import { defineStore } from 'pinia'
import { computed, ref, set } from 'vue'

import { useContext } from '@/composables/context'
import { useSelectedTrip } from '@/composables/trip'
import { Document } from '@/types/document'
import type {
  BaseTransportationUIInput,
  RouteSuggestion,
  Transportation,
  TransportationType,
  TransportationUIInput,
} from '@/types/transportation'
import type { LocationAutocompleteResult } from '@/types/trip'
import { getDefaultEmojiForTransportationType } from '@/utils/transportation'

/**
 * Extended route type that includes an emoji for visual representation on
 * the map
 * */
export type RouteWithEmoji = RouteSuggestion & { emoji: string }

/**
 * Draft state for the arrival/departure anchor leg toggles in the LegPicker.
 * `enabled` mirrors the corresponding `has{Arrival,Departure}Transportation`
 * flag on the persisted group. `location` is the user-supplied external
 * endpoint (origin for arrival, destination for departure) — required when
 * newly enabling the toggle, ignored when already-enabled remains enabled.
 */
export type GroupAnchorDraft = {
  enabled: boolean
  location?: LocationAutocompleteResult
}

/**
 * Touched-vs-untouched wrapper for LegPicker drafts. `touched: false` means
 * the user has not interacted with this control yet, so callers should fall
 * back to the persisted group state.
 */
export type Draft<T> = { touched: false } | { touched: true; value: T }

export const UNTOUCHED: Draft<never> = { touched: false }
export function touched<T>(value: T): Draft<T> {
  return { touched: true, value }
}

/**
 * Represents the selected transportation for a specific waypoint.
 */
type SelectedTransportation = {
  /** The waypoint id where the transportation is connected with */
  waypointId: string
  /** Specifies the index of the transportation in the waypoint's transportations array */
  index?: number
  transportation: TransportationUIInput
  groupSelection: Draft<string[]>
  groupArrival: Draft<GroupAnchorDraft>
  groupDeparture: Draft<GroupAnchorDraft>
}

/**
 * Store for managing transportation data and route selection in the application.
 * Handles the state for selected transportation, available routes, and provides
 * access to all transportation data across waypoints.
 * This store should be used as the single source of truth for
 * transportation-related data in the application.
 */
export const useTransportationStore = defineStore('transportation', () => {
  const { i18n } = useContext()
  const trip = useSelectedTrip()

  /**
   * This holds the currently selected transportation and is used in the
   * TransportationSlideOver and also in the map to highlight the corresponding
   * route.
   */
  const selectedTransportation = ref<SelectedTransportation>()

  /**
   * This holds all available route for the selected transportation. It also
   * includes the selected route, so that the user can switch between different
   * routes.
   */
  const availableRoutes = ref<RouteWithEmoji[]>([])
  /**
   * This flag indicates whether all available routes for the selected
   * transportation should be shown from the {@link availableRoutes} array or
   * only the selected route of
   * {@link selectedTransportation.transportation.route}.
   */
  const showAlternativeRoutes = ref<boolean>(false)

  /**
   * Flag to disable the save button while an async operation is in progress
   * (e.g. fetching routes).
   */
  const isSaveDisabled = ref(false)

  /**
   * Map of all transportations for each waypoint. The keys are waypoint IDs and
   * values is an array of transportations that are linked to that waypoint.
   */
  const allTransportations = computed<Map<string, Transportation[]>>(() => {
    const transportations = new Map<string, Transportation[]>()

    trip.value?.waypoints.forEach((waypoint) => {
      waypoint.transportations.forEach((transportation) => {
        if (!transportations.has(waypoint._id)) {
          transportations.set(waypoint._id, [])
        }
        transportations.get(waypoint._id)?.push(transportation)
      })
    })

    return transportations
  })

  /**
   * Sets the selected transportation and updates the available routes
   * Creates a deep copy of the transportation object to avoid mutating the
   * original.
   *
   * @param params.transportation - The transportation to select
   * @param params.waypointId - The ID of the waypoint where the transportation is connected to
   * @param params.index - Optional index of the transportation in the waypoint's transportations array
   */
  function selectTransportation(params: {
    transportation: TransportationUIInput
    waypointId: string
    index?: number
  }) {
    const { transportation, waypointId, index } = params

    // Always enable the save button when a new transportation is selected.
    // The flag might have been left in a disabled state by a previous form
    // interaction (e.g. an unfinished route calculation or the flight lookup),
    // so we reset it here to avoid leaking that state between transports.
    isSaveDisabled.value = false

    selectedTransportation.value = JSON.parse(
      JSON.stringify({
        transportation,
        waypointId,
        index,
        groupSelection: UNTOUCHED,
        groupArrival: UNTOUCHED,
        groupDeparture: UNTOUCHED,
      }),
    ) as SelectedTransportation

    availableRoutes.value = transportation.route
      ? [{ ...transportation.route, emoji: transportation.emoji }]
      : []
  }

  function setGroupSelection(selection: string[]) {
    if (!selectedTransportation.value) {
      return
    }
    selectedTransportation.value.groupSelection = touched([...selection])
  }

  function setGroupArrival(draft: GroupAnchorDraft) {
    if (!selectedTransportation.value) {
      return
    }
    selectedTransportation.value.groupArrival = touched({ ...draft })
  }

  function setGroupDeparture(draft: GroupAnchorDraft) {
    if (!selectedTransportation.value) {
      return
    }
    selectedTransportation.value.groupDeparture = touched({ ...draft })
  }

  /**
   * Selects a route for the currently selected transportation.
   * If the provided route is not already in the available routes, it adds it.
   *
   * @param route - The route to select
   */
  function selectRoute(route: RouteWithEmoji) {
    if (!selectedTransportation.value) {
      console.warn('No transportation selected to set the route.')
      return
    }

    if (!availableRoutes.value.some((r) => r.polyline === route.polyline)) {
      // If the route is not already in the available routes, add it
      availableRoutes.value.push(route)
    }

    /*
      Unfortunately, we cannot directly update the route property of the
      selected transportation, because of some vue 2 reactivity change
      detection of nested objects and properties that are not set in the
      initialization of the transportation object, see:
      https://v2.vuejs.org/v2/guide/reactivity.html#For-Objects
      To fix this, we use the `set` function to ensure that the
      reactivity system is aware of the change. We have to apply this
      on the transportation object of the selected transportation
      if we directly set the route property, it would not trigger the
      reactivity system. This could be a problem with multi-nested objects.
    */
    set(selectedTransportation.value, 'transportation', {
      ...selectedTransportation.value.transportation,
      route,
    })
  }

  function deselectRoute() {
    if (!selectedTransportation.value) {
      console.warn('No transportation selected to set the route.')
      return
    }

    selectedTransportation.value.transportation.route = undefined
  }

  function resetAvailableRoutes() {
    if (selectedTransportation.value) {
      selectedTransportation.value.transportation.route = undefined
    }
    availableRoutes.value = []
  }

  /**
   * Clears the selected transportation.
   */
  function clearSelectedTransportation() {
    selectedTransportation.value = undefined
    availableRoutes.value = []
    showAlternativeRoutes.value = false
    isSaveDisabled.value = false
  }

  /**
   * Retrieves a transportation by its ID from the trip's waypoints.
   *
   * @param transportationId - The ID of the transportation to retrieve
   * @returns The transportation object if found, otherwise undefined
   */
  function getTransportationById(transportationId: string) {
    for (const waypoint of trip.value.waypoints) {
      const transportation = waypoint.transportations.find(
        (t) => t._id === transportationId,
      )
      if (transportation) {
        return transportation
      }
    }
  }

  function linkDocument(document: Document) {
    if (
      !selectedTransportation.value ||
      !document._id ||
      selectedTransportation.value.transportation.documents.includes(
        document._id,
      )
    ) {
      return
    }

    selectedTransportation.value.transportation.documents.push(document._id)
  }

  function unlinkDocument(documentId: string) {
    if (!selectedTransportation.value) {
      return
    }

    selectedTransportation.value.transportation.documents =
      selectedTransportation.value.transportation.documents.filter(
        (docId) => docId !== documentId,
      )
  }

  /**
   * This function switches the transportation type of the currently selected
   * transportation. It updates the transportation object to match the new type
   * while preserving common properties.
   *
   * @param transportationType - The transportation type to switch to
   * @returns
   */
  function switchTransportationType(transportationType: TransportationType) {
    if (
      !selectedTransportation.value ||
      selectedTransportation.value.transportation.type === transportationType
    ) {
      return
    }

    // Extract only the properties that are part of all transportation types
    const currentTransportation = selectedTransportation.value.transportation

    const baseProperties: BaseTransportationUIInput = {
      documents: currentTransportation.documents,
      emoji: getDefaultEmojiForTransportationType(transportationType),
      origin: currentTransportation.origin,
      destination: currentTransportation.destination,
      label: i18n.t(`transportation.${transportationType}.label`).toString(),
      period: currentTransportation.period,
      departureDate: currentTransportation.departureDate,
      arrivalDate: currentTransportation.arrivalDate,
      comment: currentTransportation.comment,
      isArrivalTransportation: currentTransportation.isArrivalTransportation,
      _id: currentTransportation._id,
    }

    availableRoutes.value = []
    // Group selection is driving-only — reset on type switch.
    selectedTransportation.value.groupSelection = UNTOUCHED
    selectedTransportation.value.groupArrival = UNTOUCHED
    selectedTransportation.value.groupDeparture = UNTOUCHED

    if (transportationType === 'flight') {
      selectedTransportation.value.transportation = {
        ...baseProperties,
        type: transportationType,
        arrivalAirport: { iata: '' },
        departureAirport: { iata: '' },
      }
    } else if (transportationType === 'driving') {
      selectedTransportation.value.transportation = {
        type: transportationType,
        rentals: [],
        ...baseProperties,
      }
    } else {
      selectedTransportation.value.transportation = {
        ...baseProperties,
        type: transportationType,
      }
    }
  }

  return {
    // State
    selectedTransportation,
    availableRoutes,
    showAlternativeRoutes,
    isSaveDisabled,

    // Getters
    allTransportations,

    // Actions
    selectTransportation,
    selectRoute,
    deselectRoute,
    resetAvailableRoutes,
    clearSelectedTransportation,
    setGroupSelection,
    setGroupArrival,
    setGroupDeparture,
    getTransportationById,
    linkDocument,
    unlinkDocument,
    switchTransportationType,
  }
})
